import * as React from 'react'

import { IAttachment } from '~/client/graph'
import {
  FileKind,
  getFileKind,
} from '~/client/src/shared/components/FilesInput/fileKind'
import FileTypeIcon from '~/client/src/shared/components/FilesInput/FileTypeIcon'
import { AcrobatIcon } from '~/client/src/shared/components/icons/AcrobatIcon'
import { AttachmentIcon } from '~/client/src/shared/components/icons/AttachmentIcon'
import { ChevronDownIcon } from '~/client/src/shared/components/icons/ChevronDownIcon'
import { ExcelIcon } from '~/client/src/shared/components/icons/ExcelIcon'
import { FileImageIcon } from '~/client/src/shared/components/icons/FileImageIcon'
import { FileVideoIcon } from '~/client/src/shared/components/icons/FileVideoIcon'
import { PowerpointIcon } from '~/client/src/shared/components/icons/PowerpointIcon'
import { WordIcon } from '~/client/src/shared/components/icons/WordIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import { useFilePreview } from '~/client/src/shared/contexts/FilePreviewContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { openAttachmentPreview } from '~/client/src/shared/utils/filePreviewUtils'

import FilePreviewPopup from './FilePreviewPopup'

interface IFileCellProps {
  value: unknown
}

const BADGE_CLASS =
  'tw:absolute tw:-bottom-0.5 tw:-left-0.5 tw:w-4 tw:h-4 tw:bg-white tw:rounded-sm tw:border tw:border-solid tw:border-md3-outline-variant tw:flex tw:items-center tw:justify-center'

function TypeBadgeIcon({ kind }: { kind: FileKind }) {
  switch (kind) {
    case 'pdf':
      return <AcrobatIcon size="xs" />
    case 'word':
      return <WordIcon size="xs" />
    case 'excel':
      return <ExcelIcon size="xs" />
    case 'ppt':
      return <PowerpointIcon size="xs" />
    case 'image':
      return <FileImageIcon size="xs" />
    case 'video':
      return <FileVideoIcon size="xs" />
    default:
      return (
        <AttachmentIcon size="xs" className="tw:text-md3-on-surface-variant" />
      )
  }
}

const CONTAINER_CLASS =
  'tw:relative tw:w-8 tw:h-8 tw:rounded tw:border tw:border-solid tw:border-md3-outline-variant tw:overflow-hidden tw:bg-white tw:flex tw:items-center tw:justify-center tw:shrink-0'

const DOC_TYPE_COLORS: Record<string, string> = {
  word: '#2b579a',
  excel: '#217346',
  ppt: '#d24726',
  pdf: '#d24726',
}

const DOC_TYPE_ABBREVIATIONS: Record<string, string> = {
  word: 'W',
  excel: 'E',
  ppt: 'P',
  pdf: 'PDF',
}

function DocumentPreview({ kind }: { kind: FileKind }) {
  return (
    <div className="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:justify-center tw:px-1.5 tw:pt-1.5 tw:pb-2.5">
      <div className="tw:h-px tw:bg-md3-on-surface-variant/40 tw:mb-1" />
      <div className="tw:h-px tw:bg-md3-on-surface-variant/40 tw:mr-1 tw:mb-1" />
      <div className="tw:h-px tw:bg-md3-on-surface-variant/40 tw:mr-0.5" />
      <span
        className="tw:absolute tw:bottom-0.5 tw:right-1 tw:text-[6px] tw:font-bold tw:leading-none"
        style={{ color: DOC_TYPE_COLORS[kind] || '#666' }}
      >
        {DOC_TYPE_ABBREVIATIONS[kind] || '?'}
      </span>
    </div>
  )
}

function FileThumbnail({ attachment }: { attachment: IAttachment }) {
  const [imageFailed, setImageFailed] = React.useState(false)
  const kind = getFileKind(attachment.fileName)

  const badge = (
    <div className={BADGE_CLASS}>
      <TypeBadgeIcon kind={kind} />
    </div>
  )

  if (kind === 'image' && !imageFailed) {
    return (
      <div className={CONTAINER_CLASS}>
        <img
          src={attachment.url}
          alt={attachment.fileName}
          className="tw:w-full tw:h-full tw:object-cover"
          onError={() => setImageFailed(true)}
        />
        {badge}
      </div>
    )
  }

  if (kind === 'video') {
    return (
      <div className={CONTAINER_CLASS}>
        <video
          src={attachment.url}
          className="tw:w-full tw:h-full tw:object-cover"
          muted
          preload="metadata"
        />
        {badge}
      </div>
    )
  }

  if (kind === 'pdf' || kind === 'word' || kind === 'excel' || kind === 'ppt') {
    return (
      <div className={CONTAINER_CLASS}>
        <DocumentPreview kind={kind} />
        {badge}
      </div>
    )
  }

  return (
    <div className={CONTAINER_CLASS}>
      <FileTypeIcon kind={kind} />
      {badge}
    </div>
  )
}

function FilePopoverItem({
  attachment,
  attachments,
  index,
}: {
  attachment: IAttachment
  attachments: IAttachment[]
  index: number
}) {
  const { showPreview } = useFilePreview()
  const [hovered, setHovered] = React.useState(false)
  const [hoverPosition, setHoverPosition] = React.useState({ x: 0, y: 0 })

  const openFile = () => {
    openAttachmentPreview(attachments, index, showPreview)
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    setHovered(true)
    const rect = e.currentTarget.getBoundingClientRect()
    setHoverPosition({ x: rect.right, y: rect.bottom })
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openFile}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openFile()
        }
      }}
      className="tw:flex tw:items-center tw:gap-2 tw:px-2 tw:py-1.5 tw:rounded tw:cursor-pointer hover:tw:bg-md3-on-surface/4"
    >
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
      >
        <FileThumbnail attachment={attachment} />
      </div>
      <span className="tw:truncate tw:text-body-md tw:text-md3-on-surface">
        {attachment.fileName}
      </span>
      <FilePreviewPopup
        attachment={attachment}
        isVisible={hovered}
        position={hoverPosition}
      />
    </div>
  )
}

const FileCell = ({ value }: IFileCellProps) => {
  const { showPreview } = useFilePreview()

  const [hoveredAttachment, setHoveredAttachment] =
    React.useState<IAttachment | null>(null)
  const [hoverPosition, setHoverPosition] = React.useState({ x: 0, y: 0 })
  const [isHovered, setIsHovered] = React.useState(false)

  if (!value || !Array.isArray(value) || value.length === 0) {
    return <div className="tw:flex tw:h-full tw:w-full tw:items-center" />
  }

  const attachments = value as IAttachment[]

  if (attachments.length === 1) {
    const first = attachments[0]

    const handleMouseEnter = (e: React.MouseEvent) => {
      setHoveredAttachment(first)
      const rect = e.currentTarget.getBoundingClientRect()
      setHoverPosition({ x: rect.right, y: rect.bottom })
    }

    const handleMouseLeave = () => {
      setHoveredAttachment(null)
    }

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      openAttachmentPreview(attachments, 0, showPreview)
    }

    return (
      <div className="tw:flex tw:h-full tw:w-full tw:items-center tw:min-w-0">
        <div
          className="tw:flex tw:items-center tw:gap-2 tw:min-w-0 tw:overflow-hidden tw:cursor-pointer"
          onClick={handleClick}
        >
          <div
            className="tw:shrink-0"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <FileThumbnail attachment={first} />
          </div>
          <div className="tw:min-w-0">
            <TooltipWrapper content={first.fileName}>
              <div className="tw:truncate tw:underline tw:text-body-md tw:text-md3-on-surface">
                {first.fileName}
              </div>
            </TooltipWrapper>
          </div>
        </div>
        <FilePreviewPopup
          attachment={hoveredAttachment}
          isVisible={!!hoveredAttachment}
          position={hoverPosition}
        />
      </div>
    )
  }

  const label = `${
    attachments.length
  } ${Localization.translator.files.toLowerCase()}`

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation()
  }

  return (
    <div className="tw:flex tw:h-full tw:w-full tw:items-center tw:min-w-0">
      <div
        className="tw:flex tw:items-center tw:w-full"
        onClick={stopPropagation}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Popover>
          <PopoverTrigger className="tw:flex tw:items-center tw:w-full tw:bg-transparent tw:border-none tw:cursor-pointer tw:p-0">
            <span
              className={`tw:text-body-md ${
                isHovered
                  ? 'tw:underline tw:text-md3-primary'
                  : 'tw:text-md3-on-surface'
              }`}
            >
              {label}
            </span>
            <ChevronDownIcon
              size="xs"
              className="tw:ml-auto tw:text-md3-on-surface-variant tw:shrink-0"
            />
          </PopoverTrigger>
          <PopoverContent
            className="tw:max-h-60 tw:overflow-y-auto tw:w-[300px]"
            side="bottom"
            align="start"
          >
            <div className="tw:flex tw:flex-col">
              {attachments.map((attachment, index) => (
                <FilePopoverItem
                  key={`${attachment.url}-${index}`}
                  attachment={attachment}
                  attachments={attachments}
                  index={index}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export default FileCell
